import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  ArrowLeft,
  ArrowRightLeft,
  Cpu,
  Database,
  Layers,
  Send,
  Server,
  ShieldCheck,
  Workflow,
  X
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ContactForm from '../ContactForm';
import PageHeader from '../components/PageHeader';

const MetricBar = ({ label, value, width, barClass }) => (
  <div>
    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
      <span className="font-medium">{label}</span>
      <span>{value}</span>
    </div>
    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
      <div className={`h-full ${barClass}`} style={{ width }} />
    </div>
  </div>
);

const FlowNode = ({ icon: Icon, title, subtitle, tone }) => (
  <div className={`border rounded-2xl p-5 ${tone.border} ${tone.bg}`}>
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-xl ${tone.iconBg} ${tone.iconText}`}>
        <Icon size={22} />
      </div>
      <div>
        <div className="font-bold text-gray-900">{title}</div>
        <div className="text-xs text-gray-600">{subtitle}</div>
      </div>
    </div>
  </div>
);

const StepCard = ({ number, title, description }) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300 h-full">
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-lg relative flex-shrink-0">
        {number}
      </div>
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-1">{title}</h3>
        <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  </div>
);

const FeatureCard = ({ icon: Icon, title, description, colorClass }) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300 flex flex-col h-full">
    <div className={`p-3 rounded-xl mb-4 w-fit ${colorClass.bg} ${colorClass.text}`}>
      <Icon size={24} />
    </div>
    <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
  </div>
);

const MicroservicioPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const infrastructureMetrics = useMemo(() => ([
    { label: 'Latencia (CDN)', value: '< 200 ms', width: '90%', barClass: 'bg-blue-500' },
    { label: 'CPU dedicados', value: '4 núcleos', width: '80%', barClass: 'bg-indigo-500' },
    { label: 'Memoria', value: '8 GB RAM', width: '75%', barClass: 'bg-emerald-500' },
    { label: 'Almacenamiento', value: '100 GB NVMe', width: '70%', barClass: 'bg-purple-500' },
    { label: 'Tráfico saliente', value: '32 TB', width: '85%', barClass: 'bg-amber-500' }
  ]), []);

  const openPopup = () => {
    setIsPopupOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    document.body.style.overflow = 'auto';
  };

  const erpSteps = useMemo(() => t('microservicio.erp_section.steps', { returnObjects: true }), [t]);
  const advantages = useMemo(() => {
    const items = t('microservicio.advantages_section.items', { returnObjects: true });
    const icons = [Layers, ShieldCheck, Activity, Workflow];
    const colors = [
      { bg: 'bg-indigo-50', text: 'text-indigo-600' },
      { bg: 'bg-emerald-50', text: 'text-emerald-600' },
      { bg: 'bg-blue-50', text: 'text-blue-600' },
      { bg: 'bg-amber-50', text: 'text-amber-600' }
    ];
    return items.map((item, i) => ({ ...item, icon: icons[i], colorClass: colors[i] }));
  }, [t]);

  return (
    <div className="relative min-h-screen bg-transparent font-inter">
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) closePopup(); }}>
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-100">
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition"
              aria-label="Cerrar popup"
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#141414] mb-3">{t('microservicio.popup.title')}</h3>
              <p className="text-gray-600 mb-6">{t('microservicio.popup.desc')}</p>
              <ContactForm
                formData="inline/4/10oz6w"
                formId="4"
                scriptSrc="https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js"
              />
            </div>
          </div>
        </div>
      )}

      <PageHeader onOpenPopup={openPopup} />

      <main className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('microservicio.back_btn')}
          </button>

          <div className="text-center mb-20">
            <div className="inline-block bg-indigo-50 text-indigo-600 p-3 rounded-full mb-4">
              <Layers size={32} />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#141414] mb-6">{t('microservicio.title')}</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 font-medium">
              {t('microservicio.subtitle')}
            </p>
            <p className="text-gray-500 max-w-4xl mx-auto leading-relaxed">
              {t('microservicio.description')}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
              <button
                onClick={openPopup}
                className="bg-[#141414] text-white hover:bg-gray-900 font-bold py-3 px-8 rounded-full transition"
              >
                {t('microservicio.cta_design')}
              </button>
              <a
                href="#infraestructura"
                className="bg-white border border-gray-200 text-[#141414] hover:bg-gray-50 font-bold py-3 px-8 rounded-full transition"
              >
                {t('microservicio.cta_infra')}
              </a>
            </div>
          </div>

          <section className="mb-24">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-6">{t('microservicio.erp_section.title')}</h2>
            <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
              {t('microservicio.erp_section.subtitle')}
            </p>

            <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-10 mb-10">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-center">
                <div className="lg:col-span-2">
                  <FlowNode
                    icon={Database}
                    title="ERP"
                    subtitle="SAP / Oracle / Dynamics / Local"
                    tone={{ border: 'border-gray-200', bg: 'bg-gray-50', iconBg: 'bg-white', iconText: 'text-gray-700' }}
                  />
                </div>
                <div className="flex items-center justify-center text-gray-400">
                  <Send size={22} />
                </div>
                <div className="lg:col-span-2">
                  <FlowNode
                    icon={Workflow}
                    title="Microservicio"
                    subtitle="Traduce, valida y orquesta datos"
                    tone={{ border: 'border-indigo-200', bg: 'bg-indigo-50', iconBg: 'bg-white', iconText: 'text-indigo-700' }}
                  />
                </div>
              </div>
              <div className="flex items-center justify-center text-gray-400 my-6">
                <ArrowRightLeft size={22} />
              </div>
              <div className="grid grid-cols-1">
                <FlowNode
                  icon={Activity}
                  title="Bitrix24"
                  subtitle="CRM, ventas, comunicación y workflows"
                  tone={{ border: 'border-blue-200', bg: 'bg-blue-50', iconBg: 'bg-white', iconText: 'text-blue-700' }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {erpSteps.map((step, i) => (
                <StepCard
                  key={step.title}
                  number={i + 1}
                  title={step.title}
                  description={step.desc}
                />
              ))}
            </div>
          </section>

          <section className="mb-24 bg-gray-50 rounded-3xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-6">{t('microservicio.advantages_section.title')}</h2>
            <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
              {t('microservicio.advantages_section.subtitle')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {advantages.map((adv) => (
                <FeatureCard
                  key={adv.title}
                  icon={adv.icon}
                  title={adv.title}
                  description={adv.desc}
                  colorClass={adv.colorClass}
                />
              ))}
            </div>
          </section>

          <section id="infraestructura" className="mb-24 scroll-mt-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#141414] mb-6">Infraestructura Robusta y Segura</h2>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  Nuestros microservicios corren sobre una infraestructura optimizada para latencia mínima y máxima seguridad, garantizando que tus datos viajen rápido y protegidos.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-gray-700">
                    <Server size={20} className="text-blue-600" />
                    <span className="font-medium">Servidores dedicados NVMe</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <Cpu size={20} className="text-indigo-600" />
                    <span className="font-medium">Procesamiento paralelo optimizado</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <ShieldCheck size={20} className="text-emerald-600" />
                    <span className="font-medium">Encriptación de punto a punto (TLS 1.3)</span>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-xl">
                <h3 className="font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Activity size={18} className="text-blue-600" />
                  Métricas de Infraestructura
                </h3>
                <div className="space-y-6">
                  {infrastructureMetrics.map((metric) => (
                    <MetricBar
                      key={metric.label}
                      label={metric.label}
                      value={metric.value}
                      width={metric.width}
                      barClass={metric.barClass}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="bg-[#141414] rounded-[2.5rem] p-10 md:p-16 text-white text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-64 h-64 bg-blue-600/20 rounded-full blur-[100px] -ml-32 -mt-32"></div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">{t('microservicio.final_cta.title')}</h2>
              <p className="text-white/70 max-w-2xl mx-auto mb-10 text-lg">
                {t('microservicio.final_cta.desc')}
              </p>
              <button
                onClick={openPopup}
                className="bg-white text-[#141414] hover:bg-gray-100 font-bold py-4 px-12 rounded-2xl transition shadow-xl"
              >
                {t('microservicio.final_cta.btn')}
              </button>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default MicroservicioPage;

